/**
 * Menu de ações por linha (⋯). Abre para cima; fallback abaixo.
 */
import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { MoreHorizontalIcon } from '../icons';
import { AppIcon, type IconName } from '../icons/AppIcon';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useCloseOnScroll } from '../../hooks/useCloseOnScroll';
import { cn } from '../../lib/utils';

export interface ActionMenuItem {
    label: string;
    icon?: React.ReactNode | IconName;
    onClick: () => void;
    variant?: 'danger';
    show?: boolean;
    disabled?: boolean;
}

interface ActionMenuProps {
    actions: ActionMenuItem[];
    buttonClassName?: string;
    strategy?: 'fixed' | 'absolute';
}

const ITEM_H = 44;
const PAD = 8;
const GAP = 6;
/** Tailwind `w-44` — usado para clamp no viewport. */
const MENU_W = 176;

const ACTION_MENU_TRIGGER = cn(
    'inline-flex shrink-0 items-center justify-center rounded-md border border-subtle bg-card',
    'size-11 touch-manipulation text-[var(--text-secondary)]',
    'transition-[background-color,color,border-color] duration-150 ease-out',
    'hover:bg-surface-1 hover:text-brand-600 hover:border-brand-200',
    'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20',
    'md:size-9',
);

const ActionMenu: React.FC<ActionMenuProps> = ({
    actions,
    buttonClassName = '',
    strategy = 'fixed',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [style, setStyle] = useState<React.CSSProperties | null>(null);
    const visible = actions.filter((action) => action.show !== false);

    const close = useCallback(() => setIsOpen(false), []);
    useClickOutside(menuRef, close, isOpen);
    useCloseOnScroll(close, isOpen);

    useLayoutEffect(() => {
        if (!isOpen || !buttonRef.current) {
            setStyle(null);
            return;
        }
        if (strategy === 'absolute') {
            setStyle({ position: 'absolute', top: `calc(100% + ${GAP}px)`, right: 0 });
            return;
        }
        const rect = buttonRef.current.getBoundingClientRect();
        const height = visible.length * ITEM_H + PAD;
        const openDown = rect.top < height + GAP;
        const vertical = openDown
            ? { top: rect.bottom + GAP }
            : { bottom: window.innerHeight - rect.top + GAP };

        // Prefere alinhar à esquerda do botão; se não couber, ancora na direita do botão.
        // Depois clampa para não sair do viewport (mobile com ⋯ à esquerda).
        const fitsLeftAligned = rect.left + MENU_W + PAD <= window.innerWidth;
        const idealLeft = fitsLeftAligned ? rect.left : rect.right - MENU_W;
        const maxLeft = Math.max(PAD, window.innerWidth - MENU_W - PAD);
        const left = Math.min(Math.max(idealLeft, PAD), maxLeft);

        setStyle({ ...vertical, left, position: 'fixed' });
    }, [isOpen, strategy, visible.length]);

    if (visible.length === 0) return null;

    return (
        <div className="relative inline-block" ref={menuRef}>
            <button
                ref={buttonRef}
                type="button"
                title="Ações"
                aria-label="Abrir menu de ações"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={(event) => {
                    event.stopPropagation();
                    setIsOpen((open) => !open);
                }}
                className={cn(ACTION_MENU_TRIGGER, buttonClassName)}
            >
                <MoreHorizontalIcon className="size-4 shrink-0 text-current" aria-hidden />
            </button>

            {isOpen && style && (
                <div
                    role="menu"
                    aria-label="Ações"
                    className="z-[400] w-44 overflow-hidden rounded-lg border border-subtle bg-card py-1 shadow-md animate-in fade-in-0 zoom-in-95 duration-100"
                    style={style}
                >
                    {visible.map((action) => {
                        const danger = action.variant === 'danger';
                        return (
                            <button
                                key={action.label}
                                type="button"
                                role="menuitem"
                                disabled={action.disabled}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (action.disabled) return;
                                    action.onClick();
                                    close();
                                }}
                                className={cn(
                                    'flex w-full items-center gap-s3 px-s4 py-2.5 text-left text-sm transition-colors',
                                    action.disabled
                                        ? 'cursor-not-allowed text-[var(--text-muted)]'
                                        : danger
                                            ? 'text-destructive hover:bg-destructive/8'
                                            : 'text-default hover:bg-surface-1',
                                )}
                            >
                                <ItemIcon icon={action.icon} danger={danger} />
                                <span className="flex-1">{action.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

function ItemIcon({ icon, danger }: { icon: ActionMenuItem['icon']; danger: boolean }) {
    if (!icon) return null;
    const tone = danger ? 'text-destructive' : 'text-[var(--text-muted)]';
    if (typeof icon === 'string') {
        return <AppIcon name={icon as IconName} className={cn('size-4 shrink-0', tone)} aria-hidden />;
    }
    return <span className={cn('shrink-0', tone)}>{icon}</span>;
}

function item(label: string, icon: IconName, onClick: () => void, variant?: 'danger'): ActionMenuItem {
    return variant ? { label, icon, onClick, variant } : { label, icon, onClick };
}

export const createEditAction = (onClick: () => void) => item('Editar', 'edit', onClick);
export const createDeleteAction = (onClick: () => void) => item('Excluir', 'trash', onClick, 'danger');
export const createDuplicateAction = (onClick: () => void) => item('Duplicar', 'duplicate', onClick);
export const createImportAction = (onClick: () => void) => item('Importar', 'upload', onClick);
export const createExportAction = (onClick: () => void) => item('Exportar', 'download', onClick);
export const createTestAction = (onClick: () => void) => item('Run Test', 'zap', onClick);

export default ActionMenu;
